<script setup lang="ts">
/**
 * ProductNav — the global top navigation bar and app chrome. Renders the brand
 * lockup, the primary nav (marketing tabs + an authed "Play" menu), and an
 * auth-aware account control (Sign in / Get started when signed out; the user's
 * real avatar + account dropdown when signed in).
 *
 * It owns navigation for the whole app — there is no sidebar. Under 860px the
 * tabs collapse into a hamburger-triggered Sheet drawer so mobile keeps full
 * reachability. Active state is derived from the current route.
 */
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMediaQuery } from '@vueuse/core'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@dcs/ui'
import {
  GraduationCap,
  ChevronDown,
  House,
  Info,
  ListOrdered,
  LogIn,
  LogOut,
  Medal,
  Menu,
  Play,
  ShieldCheck,
  Tag,
  User,
  Wrench,
} from 'lucide-vue-next'
import NavLockup from '@/components/brand/NavLockup.vue'
import { useAuthStore } from '@/stores/auth'
import { useProfileStore } from '@/stores/profile'
import { generateAvatarSvgDataUri } from '@/lib/avatar/generator'
import { competitiveEnabled } from '@/lib/featureFlags'

type IconComponent = typeof House

interface NavItem {
  label: string
  to: string
  icon: IconComponent
  /** Route prefixes that mark this item active. Defaults to [to]. */
  matches?: string[]
  /** Only shown when the competitive flag is on. */
  competitive?: boolean
}

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const profileStore = useProfileStore()

/** Standalone media query — replaces the old useSidebar().isMobile dependency. */
const isMobile = useMediaQuery('(max-width: 860px)')

const mobileOpen = ref(false)
// Close the mobile drawer whenever the route changes (e.g. tapping a link).
watch(
  () => route.fullPath,
  () => {
    mobileOpen.value = false
  },
)

// ── Nav model ────────────────────────────────────────────────────────────────
// Marketing surfaces everyone can reach. Tournaments/Rankings are flag-gated to
// match the router + the rest of the app. Home is intentionally omitted — the
// brand lockup routes to `/`. Tournaments deep-links into the lobby's
// tournaments tab so lobby + tournaments browse flow together.
const marketingItems = computed<NavItem[]>(() =>
  [
    { label: 'Tournaments', to: '/lobby?tab=tournaments', icon: Medal, matches: ['/tournaments'], competitive: true },
    { label: 'Rankings', to: '/rankings', icon: ListOrdered, competitive: true },
    { label: 'Pricing', to: '/membership', icon: Tag },
    { label: 'About', to: '/about', icon: Info },
    { label: 'Learn', to: '/learn', icon: GraduationCap, matches: ['/learn', '/tutorial'] },
  ].filter((item) => !item.competitive || competitiveEnabled),
)

// The single authed entry point into the game. Profile is reached from the
// right-side account dropdown (desktop) / drawer foot (mobile), not here.
const lobbyItem: NavItem = {
  label: 'Lobby',
  to: '/lobby',
  icon: Play,
  matches: ['/lobby', '/game/'],
}

const adminItems: NavItem[] = [
  { label: 'Scenario Lab', to: '/scenario-lab', icon: ShieldCheck },
  { label: 'Dev Testing', to: '/dev-testing', icon: Wrench },
]

const isAuthed = computed(() => authStore.isAuthenticated)
const isAdmin = computed(() => authStore.isAdmin)

const adminNavItems = computed<NavItem[]>(() => (isAdmin.value ? adminItems : []))

function isActive(item: NavItem) {
  const matches = item.matches ?? [item.to]
  return matches.some((m) => (m === '/' ? route.path === '/' : route.path.startsWith(m)))
}

// ── Account identity (real avatar + name/email) ──────────────────────────────
const accountAvatarUrl = computed(() => {
  const prefs = profileStore.profile?.avatarPreferences
  if (prefs) {
    return generateAvatarSvgDataUri(prefs)
  }
  return authStore.user?.picture ?? ''
})

const userInitials = computed(() => {
  const name = authStore.user?.name
  if (!name) return 'U'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
  }
  return name.charAt(0).toUpperCase()
})

const displayName = computed(() => authStore.user?.name || 'Player')
const displayEmail = computed(() => authStore.user?.email || '')

// ── Actions ──────────────────────────────────────────────────────────────────
function signIn() {
  const returnTo = (route.query.returnTo as string) || route.fullPath
  authStore.login(returnTo)
}
function getStarted() {
  authStore.login('/lobby')
}
function developerLogin() {
  const returnTo = (route.query.returnTo as string) || route.fullPath
  authStore.goToDevLogin(returnTo)
}
function goProfile() {
  router.push('/profile')
}
function logout() {
  authStore.logout()
}
</script>

<template>
  <div class="product-nav tex-felt">
    <div class="product-nav__left">
      <RouterLink to="/" class="product-nav__brand" aria-label="MI Euchre home">
        <NavLockup :h="34" reversed />
      </RouterLink>

      <!-- Desktop primary nav -->
      <nav v-if="!isMobile" class="product-nav__tabs" aria-label="Primary">
        <!-- Lobby is the single authed hub for public/private games + tournaments -->
        <RouterLink
          v-if="isAuthed"
          :to="lobbyItem.to"
          class="product-nav__tab font-sans"
          :class="{ 'product-nav__tab--active': isActive(lobbyItem) }"
        >
          {{ lobbyItem.label }}
        </RouterLink>

        <RouterLink
          v-for="item in marketingItems"
          :key="item.to"
          :to="item.to"
          class="product-nav__tab font-sans"
          :class="{ 'product-nav__tab--active': isActive(item) }"
        >
          {{ item.label }}
        </RouterLink>
      </nav>
    </div>

    <div class="product-nav__right">
      <!-- Signed in: account dropdown with the user's real avatar -->
      <template v-if="isAuthed">
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <button type="button" class="product-nav__chip" aria-label="Account menu">
              <Avatar class="product-nav__ava">
                <AvatarImage
                  v-if="accountAvatarUrl"
                  :src="accountAvatarUrl"
                  :alt="`${displayName}'s Euchre avatar`"
                  class="object-cover"
                />
                <AvatarFallback class="bg-euchre-brown text-euchre-dark-teal font-bold text-sm">
                  {{ userInitials }}
                </AvatarFallback>
              </Avatar>
              <span class="product-nav__chip-name font-sans">{{ displayName }}</span>
              <ChevronDown :size="16" class="product-nav__chip-caret" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            class="min-w-[280px] bg-euchre-cream border-2 border-euchre-brown/30 rounded-2xl p-1 shadow-2xl"
            align="end"
            :side-offset="12"
          >
            <DropdownMenuLabel class="p-0 font-normal">
              <div class="flex items-center gap-3 px-3 py-3 bg-linear-to-br from-euchre-teal/10 to-euchre-brown/10 rounded-xl m-1">
                <Avatar class="w-12 h-12 border-3 border-euchre-brown shadow-md">
                  <AvatarImage
                    v-if="accountAvatarUrl"
                    :src="accountAvatarUrl"
                    :alt="displayName"
                    class="object-cover"
                  />
                  <AvatarFallback class="bg-euchre-brown text-white font-bold text-base">
                    {{ userInitials }}
                  </AvatarFallback>
                </Avatar>
                <div class="flex flex-col min-w-0 flex-1">
                  <span class="text-base font-bold text-euchre-dark-teal truncate">{{ displayName }}</span>
                  <span class="text-sm text-euchre-muted truncate leading-tight mt-0.5">{{ displayEmail }}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator class="bg-euchre-brown/20 my-1" />
            <DropdownMenuGroup class="px-1">
              <DropdownMenuItem
                class="rounded-lg text-euchre-dark-teal font-medium cursor-pointer hover:bg-euchre-teal/15 focus:bg-euchre-teal/15 px-3 py-2.5"
                @click="goProfile"
              >
                <User :size="18" class="mr-3 text-euchre-muted" />
                <span>Profile</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <template v-if="adminNavItems.length">
              <DropdownMenuSeparator class="bg-euchre-brown/20 my-1" />
              <DropdownMenuLabel class="px-3 pt-1 pb-1 text-[0.65rem] uppercase tracking-[0.2em] text-euchre-muted">
                Admin
              </DropdownMenuLabel>
              <DropdownMenuGroup class="px-1">
                <DropdownMenuItem
                  v-for="item in adminNavItems"
                  :key="item.to"
                  as-child
                  class="rounded-lg text-euchre-dark-teal font-medium cursor-pointer hover:bg-euchre-teal/15 focus:bg-euchre-teal/15 px-3 py-2.5"
                >
                  <RouterLink :to="item.to">
                    <component :is="item.icon" :size="18" class="mr-3 text-euchre-muted" />
                    <span>{{ item.label }}</span>
                  </RouterLink>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </template>
            <DropdownMenuSeparator class="bg-euchre-brown/20 my-1" />
            <div class="px-1 pb-1">
              <DropdownMenuItem
                class="rounded-lg text-euchre-rust font-semibold cursor-pointer hover:bg-euchre-rust/15 hover:text-euchre-rust-dark focus:bg-euchre-rust/15 focus:text-euchre-rust-dark px-3 py-2.5"
                @click="logout"
              >
                <LogOut :size="18" class="mr-3" />
                <span>Log out</span>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </template>

      <!-- Signed out: auth CTAs (desktop only; mobile gets them in the drawer) -->
      <template v-else-if="!isMobile">
        <button type="button" class="product-nav__signin font-sans" @click="signIn">Sign in</button>
        <button
          v-if="authStore.fakeAuthEnabled"
          type="button"
          class="product-nav__signin font-sans"
          @click="developerLogin"
        >
          Developer Login
        </button>
        <Button size="sm" class="product-nav__cta" @click="getStarted">Get started</Button>
      </template>

      <!-- Mobile hamburger → Sheet drawer. v-model:open lets the route watcher
           close it on navigation. -->
      <div v-if="isMobile" class="product-nav__mobile">
        <Sheet v-model:open="mobileOpen">
          <SheetTrigger as-child>
            <button type="button" class="product-nav__hamburger" aria-label="Open navigation menu">
              <Menu :size="22" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" class="w-[86%] max-w-[340px] bg-euchre-dark-teal border-r-2 border-gold/25 p-0">
            <SheetHeader class="px-5 pt-5 pb-3 border-b-2 border-gold/20">
              <SheetTitle class="text-left">
                <NavLockup :h="32" reversed />
              </SheetTitle>
            </SheetHeader>

            <div class="product-nav__drawer">
              <!-- Authed identity card -->
              <div v-if="isAuthed" class="product-nav__drawer-id">
                <Avatar class="h-11 w-11 shadow-lg shrink-0">
                  <AvatarImage
                    v-if="accountAvatarUrl"
                    :src="accountAvatarUrl"
                    :alt="`${displayName}'s Euchre avatar`"
                    class="object-cover"
                  />
                  <AvatarFallback class="bg-euchre-brown text-euchre-dark-teal font-bold text-sm">
                    {{ userInitials }}
                  </AvatarFallback>
                </Avatar>
                <div class="min-w-0 flex-1">
                  <div class="text-sm font-semibold text-euchre-cream truncate">{{ displayName }}</div>
                  <div class="text-xs text-euchre-brown-light/80 truncate">{{ displayEmail }}</div>
                </div>
              </div>

              <!-- Authed: single Lobby entry (public/private/tournaments live as
                   lobby tabs; Profile is in the drawer foot below) -->
              <template v-if="isAuthed">
                <div class="product-nav__drawer-label">Game Hub</div>
                <SheetClose as-child>
                  <RouterLink :to="lobbyItem.to" class="product-nav__drawer-link" :class="{ 'product-nav__drawer-link--active': isActive(lobbyItem) }">
                    <component :is="lobbyItem.icon" :size="19" />
                    <span>{{ lobbyItem.label }}</span>
                  </RouterLink>
                </SheetClose>
              </template>

              <!-- Marketing -->
              <div class="product-nav__drawer-label">Explore</div>
              <SheetClose v-for="item in marketingItems" :key="item.to" as-child>
                <RouterLink :to="item.to" class="product-nav__drawer-link" :class="{ 'product-nav__drawer-link--active': isActive(item) }">
                  <component :is="item.icon" :size="19" />
                  <span>{{ item.label }}</span>
                </RouterLink>
              </SheetClose>

              <!-- Admin -->
              <template v-if="adminNavItems.length">
                <div class="product-nav__drawer-label">Admin Tools</div>
                <SheetClose v-for="item in adminNavItems" :key="item.to" as-child>
                  <RouterLink :to="item.to" class="product-nav__drawer-link" :class="{ 'product-nav__drawer-link--active': isActive(item) }">
                    <component :is="item.icon" :size="19" />
                    <span>{{ item.label }}</span>
                  </RouterLink>
                </SheetClose>
              </template>

              <!-- Account actions — Profile lives here on mobile (no account
                   dropdown), then Log out. -->
              <div class="product-nav__drawer-foot">
                <template v-if="isAuthed">
                  <SheetClose as-child>
                    <RouterLink to="/profile" class="product-nav__drawer-action">
                      <User :size="19" />
                      <span>Profile</span>
                    </RouterLink>
                  </SheetClose>
                  <button type="button" class="product-nav__drawer-action product-nav__drawer-action--danger" @click="logout">
                    <LogOut :size="19" />
                    <span>Log out</span>
                  </button>
                </template>
                <template v-else>
                  <button type="button" class="product-nav__drawer-action" @click="signIn">
                    <LogIn :size="19" />
                    <span>Sign In with Google</span>
                  </button>
                  <button
                    v-if="authStore.fakeAuthEnabled"
                    type="button"
                    class="product-nav__drawer-action"
                    @click="developerLogin"
                  >
                    <LogIn :size="19" />
                    <span>Developer Login</span>
                  </button>
                  <Button class="product-nav__drawer-cta w-full mt-1" @click="getStarted">Get started</Button>
                </template>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  </div>
</template>

<style scoped>
.product-nav {
  position: relative;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 clamp(1rem, 3vw, 2.1rem);
  min-height: 64px;
  border-bottom: 1px solid rgb(var(--gold) / 0.3);
}
.product-nav__left {
  display: flex;
  align-items: center;
  gap: 2.2rem;
  min-width: 0;
}
.product-nav__brand {
  text-decoration: none;
  flex-shrink: 0;
}
.product-nav__right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

/* ── Desktop tabs ─────────────────────────────────────────────────────────── */
.product-nav__tabs {
  display: flex;
  align-items: center;
  gap: 1.4rem;
}
.product-nav__tab {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(243, 236, 218, 0.84);
  text-decoration: none;
  padding-bottom: 2px;
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  background: transparent;
  cursor: pointer;
  transition: color 0.15s ease;
}
.product-nav__tab:hover {
  color: rgb(var(--gold));
}
.product-nav__tab--active {
  color: rgb(var(--gold));
  font-weight: 700;
  border-bottom-color: rgb(var(--gold));
}
.product-nav__tab-caret {
  opacity: 0.7;
}

/* ── Auth CTAs ────────────────────────────────────────────────────────────── */
.product-nav__signin {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(243, 236, 218, 0.92);
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
}
.product-nav__signin:hover {
  color: rgb(var(--gold));
}
.product-nav__cta {
  background: rgb(var(--barn));
  color: rgb(var(--paper));
  font-weight: 700;
  box-shadow: 0 3px 0 rgb(var(--barn-deep));
}
.product-nav__cta:hover {
  background: rgb(var(--barn-deep));
}

/* ── Account chip ─────────────────────────────────────────────────────────── */
.product-nav__chip {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  background: transparent;
  cursor: pointer;
  padding: 0;
}
.product-nav__chip-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(243, 236, 218, 0.92);
  max-width: 9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.product-nav__chip-caret {
  color: rgba(243, 236, 218, 0.7);
}
.product-nav__ava {
  width: 36px;
  height: 36px;
  border: 2px solid rgb(var(--gold) / 0.6);
}

/* ── Mobile hamburger + drawer ───────────────────────────────────────────── */
.product-nav__mobile {
  display: flex;
  align-items: center;
}
.product-nav__hamburger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  color: rgb(var(--paper));
  background: transparent;
  cursor: pointer;
}
.product-nav__hamburger:hover {
  color: rgb(var(--gold));
  background: rgba(243, 236, 218, 0.08);
}

.product-nav__drawer {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.75rem 0.85rem 1.25rem;
  overflow-y: auto;
  height: 100%;
}
.product-nav__drawer-id {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.6rem 0.65rem 0.9rem;
}
.product-nav__drawer-label {
  margin: 0.85rem 0.65rem 0.3rem;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(217, 164, 65, 0.85);
}
.product-nav__drawer-label:first-of-type {
  margin-top: 0.25rem;
}
.product-nav__drawer-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.7rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(243, 236, 218, 0.9);
  text-decoration: none;
}
.product-nav__drawer-link:hover {
  background: rgba(243, 236, 218, 0.08);
  color: rgb(var(--paper));
}
.product-nav__drawer-link--active {
  background: rgba(217, 164, 65, 0.16);
  color: rgb(var(--gold));
  font-weight: 700;
}
.product-nav__drawer-foot {
  margin-top: auto;
  padding-top: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  border-top: 1px solid rgba(217, 164, 65, 0.2);
}
.product-nav__drawer-action {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.7rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  color: rgba(243, 236, 218, 0.92);
  background: transparent;
  cursor: pointer;
  text-align: left;
}
.product-nav__drawer-action:hover {
  background: rgba(243, 236, 218, 0.08);
  color: rgb(var(--gold));
}
.product-nav__drawer-action--danger {
  color: rgb(var(--barn));
}
.product-nav__drawer-action--danger:hover {
  background: rgba(194, 80, 47, 0.14);
  color: rgb(var(--barn-deep));
}
.product-nav__drawer-cta {
  background: rgb(var(--barn));
  color: rgb(var(--paper));
  font-weight: 700;
  box-shadow: 0 3px 0 rgb(var(--barn-deep));
}
</style>
